import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GlobalSnackbarService } from './global-snackbar.service';

/**
 * GlobalSnackbarComponent
 *
 * A persistent, navigation-independent status snackbar mounted once at
 * app-root. Driven entirely by GlobalSnackbarService — no feature-specific
 * knowledge lives here.
 *
 * Mount once in app.component.ts:
 *   <app-global-snackbar />
 *
 * Then push notifications from any service/component via:
 *   inject(GlobalSnackbarService).show({ ... });
 */
@Component({
  selector: 'app-global-snackbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    @if (snackbar.isVisible()) {
      @let n = snackbar.notification()!;
      <div
        class="g-snackbar"
        [attr.data-status]="n.status"
        role="status"
        aria-live="polite"
        aria-atomic="true">

        <!-- Left accent stripe -->
        <div class="g-snackbar__stripe"></div>

        <!-- Icon -->
        <div class="g-snackbar__icon">
          @if (n.status === 'loading') {
            <span class="g-snackbar__spinner" aria-hidden="true"></span>
          } @else if (n.status === 'done') {
            <i class="pi pi-check-circle g-snackbar__icon--done" aria-hidden="true"></i>
          } @else if (n.status === 'error') {
            <i class="pi pi-exclamation-circle g-snackbar__icon--error" aria-hidden="true"></i>
          }
        </div>

        <!-- Body -->
        <div class="g-snackbar__body">
          <div class="g-snackbar__title-row">
            <span class="g-snackbar__title">{{ n.title }}</span>
            @if (n.badge) {
              <span class="g-snackbar__badge">{{ n.badge }}</span>
            }
          </div>

          @if (n.subtitle) {
            <p class="g-snackbar__subtitle">{{ n.subtitle }}</p>
          }

          <!-- Indeterminate progress bar — only while loading -->
          @if (n.status === 'loading') {
            <div class="g-snackbar__progress" role="progressbar" aria-label="In progress">
              <div class="g-snackbar__progress-fill"></div>
            </div>
          }
        </div>

        <!-- Actions -->
        <div class="g-snackbar__actions">
          <!-- CTA (open / navigate / etc.) -->
          @if (n.action) {
            <button
              class="g-snackbar__cta"
              type="button"
              (click)="handleAction()">
              @if (n.action.icon) {
                <i [class]="n.action.icon" aria-hidden="true"></i>
              }
              {{ n.action.label }}
            </button>
          }

          <!-- Dismiss -->
          @if (n.dismissible !== false && n.status !== 'loading') {
            <button
              class="g-snackbar__dismiss"
              type="button"
              title="Dismiss"
              aria-label="Dismiss notification"
              (click)="snackbar.dismiss()">
              <i class="pi pi-times" aria-hidden="true"></i>
            </button>
          }
        </div>

      </div>
    }
  `,
  styles: [`
    /* ── Card ─────────────────────────────────────────────────────────── */
    .g-snackbar {
      position: fixed;
      bottom: 1.5rem;
      right: 1.5rem;
      z-index: 9999;

      display: flex;
      align-items: stretch;

      min-width: 320px;
      max-width: 440px;

      background: var(--surface-card);
      border: 1px solid var(--surface-border);
      border-radius: 10px;
      box-shadow:
        0 4px 6px -1px rgba(0,0,0,.07),
        0 10px 20px -3px rgba(0,0,0,.1);
      overflow: hidden;

      animation: g-snackbar-in .28s cubic-bezier(.34,1.46,.64,1) both;
    }

    @keyframes g-snackbar-in {
      from { opacity: 0; transform: translateY(14px) scale(.97); }
      to   { opacity: 1; transform: none; }
    }

    /* ── Accent stripe ─────────────────────────────────────────────────── */
    .g-snackbar__stripe {
      width: 4px;
      flex-shrink: 0;
      background: var(--primary-color);
      transition: background .2s;
    }

    .g-snackbar[data-status="done"]  .g-snackbar__stripe { background: var(--p-green-500, #22c55e); }
    .g-snackbar[data-status="error"] .g-snackbar__stripe { background: var(--p-red-500,   #ef4444); }

    /* ── Icon ──────────────────────────────────────────────────────────── */
    .g-snackbar__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      padding: 1rem 0.625rem 1rem 0.875rem;
    }

    .g-snackbar__spinner {
      display: block;
      width: 20px;
      height: 20px;
      border: 2.5px solid var(--surface-border);
      border-top-color: var(--primary-color);
      border-radius: 50%;
      animation: g-spin .7s linear infinite;
    }

    @keyframes g-spin { to { transform: rotate(360deg); } }

    .g-snackbar__icon--done  { font-size: 1.2rem; color: var(--p-green-500, #22c55e); }
    .g-snackbar__icon--error { font-size: 1.2rem; color: var(--p-red-500,   #ef4444); }

    /* ── Body ──────────────────────────────────────────────────────────── */
    .g-snackbar__body {
      flex: 1;
      min-width: 0;
      padding: .875rem .375rem .875rem 0;
      display: flex;
      flex-direction: column;
      gap: .2rem;
    }

    .g-snackbar__title-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: .375rem;
    }

    .g-snackbar__title {
      font-size: .8125rem;
      font-weight: 600;
      color: var(--text-color);
      line-height: 1.35;
    }

    /* Small pill badge */
    .g-snackbar__badge {
      font-size: .6875rem;
      font-weight: 500;
      color: var(--primary-color);
      background: color-mix(in srgb, var(--primary-color) 10%, transparent);
      border: 1px solid color-mix(in srgb, var(--primary-color) 22%, transparent);
      border-radius: 999px;
      padding: 1px 7px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
    }

    .g-snackbar__subtitle {
      margin: 0;
      font-size: .75rem;
      color: var(--text-color-secondary);
      line-height: 1.4;
    }

    /* ── Progress bar ──────────────────────────────────────────────────── */
    .g-snackbar__progress {
      margin-top: .45rem;
      height: 3px;
      border-radius: 999px;
      background: var(--surface-border);
      overflow: hidden;
    }

    .g-snackbar__progress-fill {
      height: 100%;
      background: linear-gradient(
        90deg,
        var(--primary-color) 0%,
        color-mix(in srgb, var(--primary-color) 55%, white) 50%,
        var(--primary-color) 100%
      );
      background-size: 200% 100%;
      animation: g-shimmer 1.4s ease-in-out infinite;
    }

    @keyframes g-shimmer {
      0%   { background-position: 200% center; }
      100% { background-position: -200% center; }
    }

    /* ── Actions ───────────────────────────────────────────────────────── */
    .g-snackbar__actions {
      display: flex;
      align-items: center;
      gap: .25rem;
      padding: .75rem .75rem .75rem .25rem;
      flex-shrink: 0;
    }

    /* Base button reset */
    .g-snackbar__cta,
    .g-snackbar__dismiss {
      display: inline-flex;
      align-items: center;
      gap: .3rem;
      border: none;
      cursor: pointer;
      font-family: inherit;
      font-weight: 600;
      border-radius: 6px;
      transition: background .15s, color .15s, opacity .15s;
      white-space: nowrap;
      line-height: 1;
    }

    /* CTA — filled primary */
    .g-snackbar__cta {
      font-size: .75rem;
      padding: .375rem .8rem;
      color: var(--primary-color-text, #fff);
      background: var(--primary-color);
    }

    .g-snackbar__cta:hover { opacity: .87; }

    .g-snackbar__cta .pi { font-size: .68rem; }

    /* Dismiss — ghost icon */
    .g-snackbar__dismiss {
      font-size: .68rem;
      padding: .3rem .35rem;
      color: var(--text-color-secondary);
      background: transparent;
    }

    .g-snackbar__dismiss:hover {
      color: var(--text-color);
      background: var(--surface-hover);
    }
  `]
})
export class GlobalSnackbarComponent {
  readonly snackbar = inject(GlobalSnackbarService);

  handleAction(): void {
    const action = this.snackbar.notification()?.action;
    if (action?.handler) action.handler();
  }
}
